/**
 * Supabase Configuration
 * Staffing Command Center
 * Built and maintained by Angel Carreon (acarreom)
 * Copyright © 2025. All rights reserved.
 */

import { createClient } from '@supabase/supabase-js'

// Supabase configuration for ticket creation
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "https://ylhhpkyrnsvmskpmatok.supabase.co"
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlsaGhwa3lybnN2bXNrcG1hdG9rIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQyMjQ2NzAsImV4cCI6MjA2OTgwMDY3MH0.k13pelYK8fWCzWPdr43R6Tr6gNsmR2xvwTHfUsNYnok"

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Ticket service functions
export const ticketService = {
  // Test Supabase connection
  async testConnection() {
    try {
      const { error } = await supabase
        .from('tickets')
        .select('id')
        .limit(1)

      if (error) {
        console.error('Supabase connection test failed:', error)
        if (error.message.includes('does not exist')) {
          return { 
            success: false, 
            error: 'Tickets table does not exist. Please create the table in your Supabase dashboard.' 
          }
        }
        return { success: false, error: error.message }
      }

      console.log('Supabase connection test successful')
      return { success: true }
    } catch (error) {
      console.error('Supabase connection test error:', error)
      return { success: false, error: error.message }
    }
  },

  // Check table structure
  async checkTableStructure() {
    try {
      // Try to get one record to see the structure
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .limit(1)

      if (error) {
        return { success: false, error: error.message }
      }

      // If no data, return the expected columns based on your schema
      if (!data || data.length === 0) {
        return { 
          success: true, 
          columns: [
            'id', 'title', 'group', 'priority', 'description', 'status', 
            'short_id', 'assigned_to', 'comments', 'history', 'created_at', 'updated_at'
          ]
        }
      }

      return { success: true, columns: Object.keys(data[0]) }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  // Create a new ticket in Supabase
  async createTicket(ticketData) {
    try {
      // Generate a short ID for the ticket
      const shortId = Math.random().toString(36).substring(2, 8).toUpperCase();
      
      const { data, error } = await supabase
        .from('tickets')
        .insert([{
          title: ticketData.title,
          description: ticketData.description,
          priority: ticketData.priority,
          group: ticketData.category, // Map category (SCC) to group
          sub_group: ticketData.subCategory, // Map subCategory to sub_group
          status: 'Open', // Match your default status
          short_id: shortId,
          assigned_to: ticketData.assignedTo || null,
          comments: [], // Default empty comments array
          history: [{
            action: 'created',
            timestamp: new Date().toISOString(),
            details: `Ticket created from staffing board. Floor: ${ticketData.floor}`,
            staffing_context: ticketData.staffingContext
          }], // Add creation to history
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()

      if (error) {
        console.error('Supabase error:', error)
        throw new Error(error.message)
      }

      return data[0]
    } catch (error) {
      console.error('Error creating ticket:', error)
      throw error
    }
  },

  // Get all tickets (optional - for future use)
  async getTickets() {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Supabase error:', error)
        throw new Error(error.message)
      }

      return data
    } catch (error) {
      console.error('Error fetching tickets:', error)
      throw error
    }
  }
} 